/**
 * environmentPartsStore — the ACTIVE environment's PARTS, and their save state
 * (ENG-1132).
 *
 * A multipart environment is several splats: the furniture as its own file,
 * separate from the walls, so a character can be told to "walk to the sofa".
 * This holds the working copy of that map while it is being edited, in the
 * same shape and with the same rules as environmentAnchorStore — the two
 * features sit in the same inspector, on the same row, and behaving differently
 * would be a second thing to learn for no reason.
 *
 * ── Two kinds of edit, and only one of them is deferred ─────────────────────
 * Metadata — a part's name, label, tags and transform — is a panel of fields,
 * so it follows the repo's explicit-save rule: `clean -> dirty` until someone
 * presses Save, which PATCHes the whole map.
 *
 * Adding and removing a part are NOT deferred, because they move BYTES. An
 * upload that sat pending would leave the file uploaded and the row not
 * mentioning it, and a delete that sat pending would show a part gone while its
 * object was still there. Both go straight to their own endpoint and refresh
 * from the response, so what is on screen is what the server has.
 *
 * ── Scoped to ONE environment, by SERVER asset id ───────────────────────────
 * Every mutator takes that id and REFUSES when it does not match the loaded
 * one. The inspector can be open on a different environment than the live one,
 * and parts written onto the wrong row are populated, plausible, and past every
 * empty check.
 *
 * ── The server is the source of truth ───────────────────────────────────────
 * `saved` is the last map the server acknowledged and `state` is derived
 * against it, so a failed write leaves the edit visible and dirty rather than
 * pretending it landed.
 */

import { partsEqual, readParts, serializeParts, withoutPart, withPartField } from './environmentParts.js'
import { environmentAssetId } from './environmentAssetId.js'

// The asset manager reaches this module through an extensionless specifier only
// Vite resolves, so it is imported lazily — the same recipe environmentFiles.js
// and environmentAnchorStore use, and what keeps this file loadable under
// `node --test`.
let managerOverride = null
async function manager() {
  return managerOverride ?? (await import('./CustomAssetManager.js')).CustomAssetManager
}

let apiOverride = null
async function api() {
  return apiOverride ?? import('./assetManagerApi.js')
}

/** Test seams: swap the asset manager / API client for fakes. */
export function _setManagerForTests(m) { managerOverride = m ?? null }
export function _setApiForTests(a) { apiOverride = a ?? null }

let state = {
  assetId: null,     // server id of the environment these parts belong to
  assetName: null,   // for messages — an id is not something to show a user
  parts: {},         // the working copy, including unsaved metadata edits
  saved: {},         // the last map the server acknowledged
  dropped: [],       // names the row carried that could not be read
  version: null,     // the row version, for If-Match on the byte-moving calls
  state: 'clean',    // clean | dirty | saving | error   (DirtyBadge's vocabulary)
  busy: null,        // 'adding' | 'removing' | null — a byte-moving call in flight
  error: null,
}

const listeners = new Set()
function emit() { for (const fn of listeners) fn() }
function set(patch) {
  state = { ...state, ...patch }
  emit()
}

/** Derive clean/dirty from the working copy against what the server has. */
function settled(parts, saved) {
  return partsEqual(parts, saved) ? 'clean' : 'dirty'
}

export const environmentPartsStore = {
  getState() { return state },
  subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn) },

  /**
   * Adopt an environment row as the subject.
   *
   * A reload of the SAME environment keeps unsaved edits — the row is re-read
   * on every content-browser refresh and on every PATCH of an unrelated field,
   * and dropping half-edited part metadata on each of those would make the
   * panel unusable. A DIFFERENT environment replaces everything.
   */
  load(asset) {
    // environmentAssetId is the documented owner of the registry's
    // `environment:<uuid>` unwrapping: a namespaced id reaching the PATCH is a
    // 400 the server calls "invalid asset id" (see environmentAssetId.js).
    const assetId = environmentAssetId(asset)
    const { parts, dropped } = readParts(asset)
    const version = asset?.version ?? asset?.raw?.version ?? null
    if (assetId && assetId === state.assetId) {
      set({ saved: parts, dropped, version, state: settled(state.parts, parts) })
      return
    }
    set({
      assetId,
      assetName: asset?.name ?? null,
      parts,
      saved: parts,
      dropped,
      version,
      state: 'clean',
      busy: null,
      error: null,
    })
  },

  /** Forget the subject (no environment selected). */
  clear() {
    set({
      assetId: null, assetName: null, parts: {}, saved: {}, dropped: [],
      version: null, state: 'clean', busy: null, error: null,
    })
  },

  /** Edit one field of one part (label, tags, pose). Dirty until saved. */
  setField(assetId, name, field, value) {
    if (!this._owns(assetId)) return
    try {
      const parts = withPartField(state.parts, name, field, value)
      set({ parts, state: settled(parts, state.saved), error: null })
    } catch (err) {
      set({ error: String(err?.message ?? err), state: 'error' })
    }
  },

  /**
   * PATCH the working copy's metadata. Resolves true on success.
   *
   * `saved` moves only on a server acknowledgement, so a failure leaves the
   * panel dirty with the edit intact — never a green badge over a write that
   * did not happen.
   */
  async save(assetId) {
    if (!this._owns(assetId)) return false
    if (state.state === 'saving') return false
    let body
    try {
      body = serializeParts(state.parts)
    } catch (err) {
      set({ error: String(err?.message ?? err), state: 'error' })
      return false
    }
    const sending = state.parts
    set({ state: 'saving', error: null })
    try {
      const m = await manager()
      await m.updateEnvironmentAsset(assetId, { parts: body })
      // Another edit may have landed while the request was in flight; settle
      // against what is on screen NOW, not against what was sent.
      set({ saved: sending, state: settled(state.parts, sending) })
      return true
    } catch (err) {
      set({ error: String(err?.message ?? err), state: 'error' })
      return false
    }
  },

  /** Throw away unsaved metadata edits. */
  revert() {
    set({ parts: state.saved, state: 'clean', error: null })
  },

  /**
   * Upload files as new parts. Immediate — it moves bytes.
   *
   * The names and the seeding of the environment's own file are the SERVER's
   * job, not this store's: it is the only side that knows what is already on
   * the row and where the bytes landed. Guessing here would mean two places
   * deriving a name from a filename, and eventually two different answers.
   *
   * Refuses while metadata is dirty. The response replaces the whole map, so an
   * unsaved rename would be silently discarded by a successful upload — which
   * is the stale-draft failure, arrived at from the other direction.
   */
  async addFiles(assetId, files) {
    if (!this._owns(assetId)) return false
    if (!files || files.length === 0) return false
    if (state.state === 'dirty') {
      set({ error: 'Save or revert the part edits first — adding a part replaces the whole list.', state: 'error' })
      return false
    }
    if (state.busy) return false
    set({ busy: 'adding', error: null })
    try {
      const { addOrgEnvironmentParts } = await api()
      const form = new FormData()
      for (const f of files) form.append('file', f, f.name)
      const row = await addOrgEnvironmentParts(assetId, form, { version: state.version })
      this._adopt(assetId, row)
      return true
    } catch (err) {
      set({ busy: null, error: String(err?.message ?? err), state: 'error' })
      return false
    }
  },

  /** Remove a part and delete its file. Immediate, for the same reason. */
  async removePart(assetId, name) {
    if (!this._owns(assetId)) return false
    if (state.busy) return false
    // Drop the metadata edit for the part being removed first, so a dirty
    // badge cannot outlive the thing it described.
    const working = withoutPart(state.parts, name)
    set({ busy: 'removing', parts: working, error: null })
    try {
      const { deleteOrgEnvironmentPart } = await api()
      const row = await deleteOrgEnvironmentPart(assetId, name, { version: state.version })
      this._adopt(assetId, row)
      return true
    } catch (err) {
      // Put it back: the server still has it, and a row missing from the panel
      // while present on the server is a lie about what the environment is.
      set({ busy: null, parts: state.saved, error: String(err?.message ?? err), state: 'error' })
      return false
    }
  },

  /**
   * Adopt the row a byte-moving call returned, and push it at the live
   * environment when it IS the live one.
   *
   * The push is what makes an upload visible: the store the renderer reads is
   * liveTuneStore, and nothing else would tell it a part appeared. It is
   * skipped for a row that is not loaded — retargeting the viewport to whatever
   * inspector happens to be open is the substitution this whole store refuses.
   */
  _adopt(assetId, row) {
    const { parts, dropped } = readParts(row)
    set({
      parts,
      saved: parts,
      dropped,
      version: row?.version ?? state.version,
      state: 'clean',
      busy: null,
      error: null,
    })
    // Lazy, and failure-tolerant: this is a courtesy refresh of the viewport,
    // not part of the write. It must not turn a successful upload into an error.
    ;(async () => {
      try {
        const [{ liveTuneStore }, { partList }] = await Promise.all([
          import('./liveTuneStore.js'),
          import('./environmentParts.js'),
        ])
        if (liveTuneStore.getState()?.environment?.assetId !== assetId) return
        liveTuneStore.setStateEphemeral({ environment: { parts: partList(parts) } })
      } catch (err) {
        console.warn('[environmentPartsStore] could not refresh the live environment:', err?.message ?? err)
      }
    })()
  },

  /** Internal: is `assetId` the subject? Records the refusal when it is not. */
  _owns(assetId) {
    if (assetId && assetId === state.assetId) return true
    set({
      error: state.assetId
        ? `part edit addressed environment ${assetId}, but ${state.assetName ?? state.assetId} is loaded`
        : 'no environment is loaded',
      state: 'error',
    })
    return false
  },
}

if (typeof window !== 'undefined') window.__ENV_PARTS__ = environmentPartsStore
